import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Button, Collapse, Flex, Grid, Menu, MenuButton, MenuItemOption, MenuList, MenuOptionGroup, Spinner, Table, TableContainer,
  Tbody, Td, Text, Th, Thead, Tr, useDisclosure, useToast,
} from '@chakra-ui/react';
import { FiAlertTriangle, FiCalendar, FiChevronDown, FiInfo, FiTag } from 'react-icons/fi';
import useAuth from 'hooks/useAuth';
import {
  MONTHS_SHORT, REVIEW_ROLES, REVIEW_LABEL, getFinanceOptions, getOverview, reviewMovement, errorMessage, formatDay, formatMillions,
  formatMoney, monthLabel,
} from 'api/administrador/finance';
import { Card, KpiTile, MonthStepper, PageHeader, PeriodStatusTag, Tag } from 'components/finance/ui';
import { CategoryBars, PairBars } from 'components/finance/charts';
import PromptModal from 'components/finance/PromptModal';

const PERIODS = [
  { value: 'month', label: 'Este mes' },
  { value: 'prev', label: 'Mes anterior' },
  { value: 'quarter', label: 'Últimos 3 meses' },
  { value: 'year', label: 'Este año' },
];

function periodTitle(data, period, ref) {
  if (!data) return monthLabel(ref.year, ref.month);
  if (period === 'month' || period === 'prev') return monthLabel(data.to.year, data.to.month);
  return `${MONTHS_SHORT[data.from.month - 1]} – ${MONTHS_SHORT[data.to.month - 1]} ${data.to.year}`;
}

function AlertRow({ tone, icon, title, children }) {
  const colors = { red: ['red.50', 'red.100', 'red.600'], orange: ['orange.50', 'orange.100', 'orange.600'], gray: ['gray.50', 'gray.200', 'gray.600'] }[tone];
  return (
    <Flex gap={3} align="flex-start" p={3} borderRadius="lg" bg={colors[0]} border="1px solid" borderColor={colors[1]}>
      <Box color={colors[2]} mt="2px">{icon}</Box>
      <Box>
        <Text fontSize="sm" fontWeight="600">{title}</Text>
        <Text fontSize="13px" color="gray.600">{children}</Text>
      </Box>
    </Flex>
  );
}

/** Admin › Finanzas › Resumen: consolidado de bancos y flujo de caja para el superadmin. */
export default function FinanzasResumen() {
  const toast = useToast();
  const { user } = useAuth();
  const canReview = REVIEW_ROLES.includes(user?.role?.type);
  const [today, setToday] = useState(null);
  const [ref, setRef] = useState(null);
  const [period, setPeriod] = useState('month');
  const [data, setData] = useState(null);
  const [prompt, setPrompt] = useState(null);
  const history = useDisclosure();

  useEffect(() => {
    getFinanceOptions()
      .then((o) => {
        const t = { year: Number(o.today.slice(0, 4)), month: Number(o.today.slice(5, 7)) };
        setToday(t);
        setRef(t);
      })
      .catch((e) => toast({ status: 'error', title: errorMessage(e) }));
  }, [toast]);

  const load = useCallback(async () => {
    if (!ref) return;
    try {
      setData(await getOverview({ period, year: ref.year, month: ref.month }));
    } catch (e) {
      toast({ status: 'error', title: errorMessage(e) });
    }
  }, [ref, period, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const review = async (id, status, comment) => {
    try {
      await reviewMovement(id, status, comment);
      load();
    } catch (e) {
      toast({ status: 'error', title: errorMessage(e) });
    }
  };

  if (!ref) return <Flex justify="center" py={20}><Spinner /></Flex>;

  const single = period === 'month' || period === 'prev';
  const alerts = data?.alerts || [];
  const caja = alerts.find((a) => a.type === 'caja_sobre_maximo' || a.type === 'caja_bajo_minimo');
  const missing = alerts.find((a) => a.type === 'falta_caja_inicial');
  const unexplained = alerts.filter((a) => a.type === 'diferencia_sin_explicar');
  const series = (data?.series || []).map((s) => ({ label: MONTHS_SHORT[s.month - 1], in: s.in, out: s.out, foot: `${s.in - s.out < 0 ? '−' : '+'}${formatMillions(Math.abs(s.in - s.out))}` }));

  return (
    <Box bg="gray.50" mx="-20px" mt="-20px" p={{ base: 4, md: 8 }} minH="calc(100vh - 80px)">
      <PageHeader
        breadcrumb={[{ label: 'Finanzas' }, { label: 'Resumen' }]}
        title="Resumen financiero"
        subtitle="Consolidado de bancos y flujo de caja."
        actions={
          <Flex gap={2} align="center">
            {single && period === 'month' ? (
              <MonthStepper value={ref} onChange={setRef} max={today} />
            ) : (
              <Flex align="center" h="32px" px={3} border="1px solid" borderColor="gray.200" borderRadius="md" bg="white" fontSize="sm" fontWeight="600">
                {periodTitle(data, period, ref)}
              </Flex>
            )}
            <Menu>
              <MenuButton as={Button} size="sm" variant="outline" bg="white" leftIcon={<FiCalendar />} rightIcon={<FiChevronDown />}>
                {PERIODS.find((p) => p.value === period).label}
              </MenuButton>
              <MenuList fontSize="sm">
                <MenuOptionGroup type="radio" value={period} onChange={(v) => { setPeriod(v); if (v !== 'month') setRef(today); }}>
                  {PERIODS.map((p) => <MenuItemOption key={p.value} value={p.value}>{p.label}</MenuItemOption>)}
                </MenuOptionGroup>
              </MenuList>
            </Menu>
          </Flex>
        }
      />

      {!data ? (
        <Flex justify="center" py={16}><Spinner /></Flex>
      ) : (
        <Flex direction="column" gap={5}>
          <Grid templateColumns={{ base: '1fr 1fr', lg: 'repeat(5, minmax(0, 1fr))' }} gap={4}>
            <KpiTile label="Liquidez total" value={formatMoney(data.liquidity)} hint="Cuentas con caja inicial; la tarjeta no suma" />
            <KpiTile label="Ingresos" value={formatMoney(data.totals.in)} hint={`${data.totals.countIn} movimientos · sin los de paso`} tone="good" />
            <KpiTile label="Egresos" value={formatMoney(data.totals.out)} hint={`${data.totals.countOut} movimientos · sin los de paso`} />
            <KpiTile label="Neto" value={formatMoney(data.totals.net)} hint={data.totals.net < 0 ? 'Salió más de lo que entró' : 'Entró más de lo que salió'} tone={data.totals.net < 0 ? 'bad' : undefined} />
            <KpiTile label="Meses de caja" value={data.monthsOfCash === null ? '—' : data.monthsOfCash.toLocaleString('es-CO')} hint={data.monthsOfCash === null ? 'Se calcula con 3 meses de registros' : 'Liquidez ÷ egreso promedio de 3 meses'} />
          </Grid>

          <Card title="Saldo por cuenta" description="Caja inicial que configuró Finanzas, movimientos del periodo y saldo calculado por el sistema." padding={0}>
            <TableContainer>
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th bg="gray.50">Cuenta</Th>
                    <Th bg="gray.50">Vista</Th>
                    <Th bg="gray.50" isNumeric>Caja inicial</Th>
                    <Th bg="gray.50" isNumeric>Ingresos</Th>
                    <Th bg="gray.50" isNumeric>Egresos</Th>
                    <Th bg="gray.50" isNumeric>Saldo calculado</Th>
                    <Th bg="gray.50">Estado</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {data.accounts.map((a) => (
                    <Tr key={a.account.id}>
                      <Td>
                        <Text fontWeight="600">{a.account.name}</Text>
                        <Text fontSize="xs" color="gray.500">{a.account.currency === 'USD' ? 'Dólares · TRM' : a.account.kind === 'caja' ? 'Pesos · efectivo' : 'Pesos'}</Text>
                      </Td>
                      <Td color="gray.600">{a.account.book === 'bancos' ? 'Bancos' : 'Flujo de caja'}</Td>
                      <Td isNumeric>{formatMoney(a.opening, a.account.currency)}</Td>
                      <Td isNumeric>{a.opening === null ? '—' : formatMoney(a.in, a.account.currency)}</Td>
                      <Td isNumeric>{a.opening === null ? '—' : formatMoney(a.out, a.account.currency)}</Td>
                      <Td isNumeric fontWeight="600">{formatMoney(a.closing, a.account.currency)}</Td>
                      <Td>
                        {caja && caja.account === a.account.name ? (
                          <Tag bg="red.100" color="red.800">{caja.type === 'caja_sobre_maximo' ? 'Por encima del máximo' : 'Por debajo del mínimo'}</Tag>
                        ) : (
                          <PeriodStatusTag status={a.status} />
                        )}
                      </Td>
                    </Tr>
                  ))}
                  <Tr>
                    <Td colSpan={5} fontWeight="700" borderBottom={0}>Liquidez total</Td>
                    <Td isNumeric fontWeight="700" borderBottom={0}>{formatMoney(data.liquidity)}</Td>
                    <Td fontSize="xs" color="gray.500" borderBottom={0}>Dólares convertidos con la última TRM</Td>
                  </Tr>
                </Tbody>
              </Table>
            </TableContainer>
          </Card>

          <Grid templateColumns={{ base: '1fr', xl: 'repeat(2, minmax(0, 1fr))' }} gap={4}>
            <PairBars title="Ingresos y egresos por mes" subtitle="Últimos 6 meses, en millones · debajo, el neto" items={series} />
            <CategoryBars data={data.byCategory} subtitle={`${periodTitle(data, period, ref)} · todas las cuentas, en millones`} />
          </Grid>

          <Grid templateColumns={{ base: '1fr', xl: 'repeat(2, minmax(0, 1fr))' }} gap={4}>
            <Card title="Movimientos grandes por revisar" description="Egresos de más de $5.000.000 en el periodo">
              {data.big.length === 0 ? (
                <Text fontSize="sm" color="gray.500">No hay egresos grandes en este periodo.</Text>
              ) : (
                data.big.map((b) => (
                  <Flex key={b.id} justify="space-between" align="center" gap={3} py={2.5} borderBottom="1px solid" borderColor="gray.100" wrap="wrap">
                    <Box minW={0}>
                      <Text fontSize="sm" fontWeight="600">{b.concept}</Text>
                      <Text fontSize="xs" color="gray.500">{formatDay(b.date)} · {b.category || 'Sin categoría'} · {b.account}</Text>
                      {b.reviewStatus === 'observado' && b.reviewComment && <Text fontSize="xs" color="orange.700">Observación: «{b.reviewComment}»</Text>}
                    </Box>
                    <Flex align="center" gap={2}>
                      <Text fontSize="sm" fontWeight="600" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatMoney(b.amount)}</Text>
                      {REVIEW_LABEL[b.reviewStatus] && <Tag bg={b.reviewStatus === 'observado' ? 'orange.100' : 'green.100'} color={b.reviewStatus === 'observado' ? 'orange.800' : 'green.800'}>{REVIEW_LABEL[b.reviewStatus]}</Tag>}
                      {canReview && b.reviewStatus === 'ninguno' && (
                        <>
                          <Button size="xs" variant="outline" onClick={() => review(b.id, 'revisado')}>Revisado</Button>
                          <Button size="xs" variant="outline" onClick={() => setPrompt({ id: b.id })}>Observar</Button>
                        </>
                      )}
                    </Flex>
                  </Flex>
                ))
              )}
            </Card>

            <Card title="Alertas" description="Lo que Finanzas debe resolver antes de cerrar el mes">
              <Flex direction="column" gap={2.5}>
                {caja && (
                  <AlertRow tone="red" icon={<FiAlertTriangle />} title={caja.type === 'caja_sobre_maximo' ? 'Caja menor por encima del máximo' : 'Caja menor por debajo del mínimo'}>
                    Tiene {formatMoney(caja.balance)} y el {caja.type === 'caja_sobre_maximo' ? 'máximo' : 'mínimo'} es {formatMoney(caja.limit)}.{' '}
                    <Text as={RouterLink} to="/plataforma/administrador/finanzas/flujo-caja" color="purple.600">Ver flujo de caja</Text>
                  </AlertRow>
                )}
                {unexplained.map((u) => (
                  <AlertRow key={`${u.account}-${u.month}`} tone="orange" icon={<FiInfo />} title={`Diferencia sin explicar en ${u.account}`}>
                    La caja inicial de {monthLabel(u.year, u.month).toLowerCase()} quedó {formatMoney(Math.abs(u.diff))} {u.diff < 0 ? 'por debajo' : 'por encima'} del cierre calculado.{' '}
                    <Text as={RouterLink} to="/plataforma/administrador/finanzas/bancos" color="purple.600">Ver bancos</Text>
                  </AlertRow>
                ))}
                {missing && (
                  <AlertRow tone="gray" icon={<FiCalendar />} title={`${missing.accounts.length} ${missing.accounts.length === 1 ? 'cuenta' : 'cuentas'} sin caja inicial`}>
                    {missing.accounts.join(', ')}. Mientras no la tengan, no suman a la liquidez.
                  </AlertRow>
                )}
                {data.uncategorized > 0 && (
                  <AlertRow tone="orange" icon={<FiTag />} title={`${data.uncategorized.toLocaleString('es-CO')} movimientos sin categoría`}>
                    Hasta asignarles categoría aparecen como «Sin categoría» en las gráficas.{' '}
                    <Text as={RouterLink} to="/plataforma/administrador/finanzas/asignar-categorias" color="purple.600">Asignar en bloque</Text>
                  </AlertRow>
                )}
                {data.historicDiffs > 0 && (
                  <Box p={3} borderRadius="lg" bg="gray.50" border="1px solid" borderColor="gray.200">
                    <Button variant="link" size="sm" color="gray.800" rightIcon={<FiChevronDown />} onClick={history.onToggle}>
                      Diferencias históricas del Excel · {data.historicDiffs} meses
                    </Button>
                    <Collapse in={history.isOpen}>
                      <Text fontSize="13px" color="gray.600" mt={2}>
                        La caja inicial escrita en el Excel no coincidió con el cierre del mes anterior en los meses migrados. Quedan «Sin explicar» y Finanzas las puede explicar desde Bancos.
                      </Text>
                    </Collapse>
                  </Box>
                )}
                {!caja && !unexplained.length && !missing && !data.uncategorized && !data.historicDiffs && (
                  <Text fontSize="sm" color="gray.500">Nada pendiente.</Text>
                )}
              </Flex>
            </Card>
          </Grid>
        </Flex>
      )}

      <PromptModal
        isOpen={!!prompt}
        onClose={() => setPrompt(null)}
        title="Observar movimiento"
        description="Finanzas ve tu observación en el movimiento y en el filtro «Observados»."
        label="Qué observas"
        confirmLabel="Observar"
        onConfirm={(comment) => review(prompt.id, 'observado', comment)}
      />
    </Box>
  );
}
